import * as React from 'react';
import { CurrencyDollarIcon, StarIcon } from '@heroicons/react/20/solid';

import { useTrack } from '@blockworks/platform/services/analytics';
import { DateFormat, ShortRelativeTime } from '@blockworks/ui/date-time';
import { ImageWithFallback } from '@blockworks/ui/image-with-fallback';
import { Link } from '@blockworks/ui/legacy/link';
import { cn } from '@blockworks/ui/utils';

import GrantsExternalLink from '@/components/pages/grants/components/grant-external-link';
import addRefParam from '@/components/pages/grants/utils/add-ref-param';
import assetLink from '@/components/pages/grants/utils/asset-link';
import { GrantsEntities, IndividualGrant } from '@/types/pages/api/grants/grants-entities';

// Checks if the link is openable through an iframe embed by checking the link for certain patterns
const isIframable = (link: string) => {
    const searchPatterns = ['dydxgrants.co', 'questbook', 'hsforms', 'typeform'];

    for (const search of searchPatterns) {
        if (link.indexOf(search) !== -1) {
            return true;
        }
    }

    return false;
};

const IndividualGrantsRow = (props: {
    item: IndividualGrant;
    assets: GrantsEntities['ecosystemsById'];
    onSelectCategory: (category: string) => void;
    onSelectGrantsType: (type: string) => void;
    className?: string;
    featured?: boolean;
    openPopup: (title: string, project: string, url: string) => void;
}) => {
    const track = useTrack();
    const { item, assets, onSelectCategory, onSelectGrantsType, className, featured, openPopup } = props;

    return (
        <tr key={item.title} className={cn('hover:bg-gray-50', featured ? 'bg-purple-50' : '', className)}>
            <td className="whitespace-nowrap px-0.5 md:px-2 py-3 md:pr-10">
                <div
                    className="flex justify-start items-center self-stretch relative gap-2"
                    title={item.project?.title}
                >
                    {assets[item.project?.assetCode || ''] && assets[item.project?.assetCode || '']?.imageUrl ? (
                        <ImageWithFallback
                            src={assets[item.project?.assetCode || '']?.imageUrl || ''}
                            className="w-[25px] h-[25px] rounded-full block"
                            alt={assets[item.project?.assetCode || '']?.code || ''}
                            fallbackSrc="/images/Blockworks_Meta_Logo.png"
                            width={32}
                            height={32}
                        />
                    ) : (
                        <ImageWithFallback
                            src={item.project?.imageUrl || ''}
                            className="w-[25px] h-[25px] rounded-full block"
                            alt={assets[item.project?.assetCode || '']?.code || ''}
                            fallbackSrc="/images/Blockworks_Meta_Logo.png"
                            width={32}
                            height={32}
                        />
                    )}

                    <div className="hidden sm:block">
                        <p className="text-sm text-left text-[#110b29]">
                            {assets[item.project?.assetCode || ''] ? (
                                <Link
                                    href={assetLink(item.project?.assetCode || '', assets)}
                                    className="hover:text-black"
                                >
                                    {item.project?.title}
                                </Link>
                            ) : (
                                item.project?.title
                            )}
                        </p>
                    </div>
                </div>
            </td>
            <td className="px-2 align-middle relative">
                <div className="flex flex-col gap-1 max-w-[220px] md:max-w-[320px] lg:max-w-[420px] xl:max-w-[650px] 2xl:max-w-[800px]">
                    <div className="flex gap-0.5 items-center">
                        {featured && (
                            <span title="Featured">
                                <StarIcon className="w-3 h-3 text-brand" />
                            </span>
                        )}
                        <GrantsExternalLink
                            href={addRefParam(item.moreInformation)}
                            text={item.title}
                            className="text-sm font-semibold whitespace-nowrap w-full"
                            description={item.description}
                            onClick={() =>
                                track('Grants.ClickedTitle', {
                                    project: item.project?.title,
                                    assetCode: item.project?.assetCode,
                                    title: item.title,
                                })
                            }
                            hideExternalLinkIcon
                        />
                    </div>
                    <div className="flex gap-3">
                        {item.lastUpdated && (
                            <span className="text-gray-400 font-light text-xs">
                                Updated <ShortRelativeTime date={new Date(item.lastUpdated).getTime() / 1000} /> ago
                            </span>
                        )}
                        <span className="text-xs text-gray-400 flex gap-0">
                            {[...Array(item.reward.length)].map((_i, index) => (
                                <CurrencyDollarIcon key={index} className="w-4 h-4" />
                            ))}
                        </span>
                    </div>
                </div>
            </td>
            <td className="px-1 whitespace-nowrap text-sm align-middle font-semibold hidden lg:table-cell">
                <div className="flex flex-col justify-center items-start self-stretch flex-grow gap-2">
                    <div className="flex flex-wrap xl:flex-nowrap justify-start items-start gap-1">
                        {item.categories.map(grantsType => (
                            <button
                                key={grantsType}
                                onClick={ev => {
                                    onSelectGrantsType(grantsType);
                                    ev.stopPropagation();
                                }}
                                className="inline-flex items-center hover:border-purple-300 border border-purple-100 cursor-pointer bg-purple-50 px-2 py-4.5 py-0.5 text-xs text-purple-700 font-semibold"
                            >
                                {grantsType}
                            </button>
                        ))}
                    </div>
                </div>
            </td>
            <td className="whitespace-nowrap px-1 text-sm align-middle font-semibold hidden lg:table-cell">
                <div className="flex flex-wrap xl:flex-nowrap justify-start items-start gap-1">
                    {item.project?.categories.map(category => (
                        <button
                            key={category}
                            onClick={ev => {
                                onSelectCategory(category);
                                ev.stopPropagation();
                            }}
                            className="inline-flex items-center hover:border-gray-300 border border-transparent cursor-pointer bg-gray-100 px-2 py-4.5 py-0.5 text-xs text-gray-700 font-semibold"
                        >
                            {category}
                        </button>
                    ))}
                </div>
            </td>
            <td className="whitespace-nowrap px-1 text-xs align-middle text-gray-700 hidden lg:table-cell">
                {item.deadline ? (
                    <span className="font-semibold">
                        <DateFormat date={item.deadline} />
                    </span>
                ) : (
                    '-'
                )}
            </td>
            <td className="px-1 pr-6 align-center">
                {item.applyUrl && (
                    <div className="flex justify-end w-full">
                        <a
                            onClick={() => {
                                track('Grants.Apply', {
                                    project: item.project?.title,
                                    assetCode: item.project?.assetCode,
                                    title: item.title,
                                    applyUrl: item.applyUrl,
                                });
                                if (isIframable(item.applyUrl)) {
                                    openPopup(item.title, item.project?.title || '', addRefParam(item.applyUrl));
                                }
                            }}
                            href={!isIframable(item.applyUrl) ? addRefParam(item.applyUrl) : undefined}
                            target="_blank"
                            rel="nofollow noreferrer"
                            className="cursor-pointer inline-flex items-center border border-gray-300 bg-white px-4 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                        >
                            Apply
                        </a>
                    </div>
                )}
            </td>
        </tr>
    );
};
export default IndividualGrantsRow;
